import React, { useEffect, useState } from 'react';
import FileSelect from './FileSelect';
import { subscribe, getCached, refresh } from '../services/fileDataService';

// Interpretation rule:
// - For each timeframe we compute 'value' = latest.CE - latest.PE
// - We compute a weighted sum across timelines (weights chosen to favor the 15m "first" timeline)
// - If sum > 0 => BUY, < 0 => SELL, = 0 => NEUTRAL

const BuysignalScreen = () => {
  const [baseFile, setBaseFile] = useState(localStorage.getItem('buysignal.base') || '');
  const [firstFile, setFirstFile] = useState(localStorage.getItem('buysignal.first') || '');
  const [thirdFile, setThirdFile] = useState(localStorage.getItem('buysignal.third') || '');

  // store latest raw record (object with CE, PE, etc.) for each timeline
  const [baseLatest, setBaseLatest] = useState(null);
  const [firstLatest, setFirstLatest] = useState(null);
  const [thirdLatest, setThirdLatest] = useState(null);

  // store parsed minutes for each selected file (e.g., 3, 5, 15, 30)
  const [baseMin, setBaseMin] = useState(null);
  const [firstMin, setFirstMin] = useState(null);
  const [thirdMin, setThirdMin] = useState(null);

  // helper: parse minutes from filename like 'NIFTY_3min_r.json' -> 3
  const parseMinutes = (file) => {
    if (!file || typeof file !== 'string') return null;
    const m = file.match(/_(\d+)min(?:_|\.|$)/i);
    if (m && m[1]) return parseInt(m[1], 10);
    return null;
  };

  useEffect(() => {
    // watch localStorage changes (in case FileSelect updates them) and
    // listen to in-page custom events for immediate updates
    const onStorage = () => {
      setBaseFile(localStorage.getItem('buysignal.base') || '');
      setFirstFile(localStorage.getItem('buysignal.first') || '');
      setThirdFile(localStorage.getItem('buysignal.third') || '');
    };
    const onChange = (e) => {
      if (!e || !e.detail) return;
      const { key, value } = e.detail;
      if (key === 'buysignal.base') setBaseFile(value || '');
      if (key === 'buysignal.first') setFirstFile(value || '');
      if (key === 'buysignal.third') setThirdFile(value || '');
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener('buysignal-change', onChange);
    // also call once to pick up current values
    onStorage();
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('buysignal-change', onChange);
    };
  }, []);

  // refresh helper to force service to fetch latest for selected files
  const refreshAll = () => {
    if (baseFile) refresh(baseFile);
    if (firstFile) refresh(firstFile);
    if (thirdFile) refresh(thirdFile);
  };

  useEffect(() => {
    let unsubBase = null;
    let unsubFirst = null;
    let unsubThird = null;

    const getLatest = (arr) => {
      if (!Array.isArray(arr) || arr.length === 0) return null;
      // dataset is newest-first; take index 0
      return arr[0];
    };

    if (baseFile) {
      const cached = getCached(baseFile);
      setBaseLatest(getLatest(cached));
      setBaseMin(parseMinutes(baseFile));
      unsubBase = subscribe(baseFile, (d) => setBaseLatest(getLatest(d)));
    } else {
      setBaseLatest(null);
      setBaseMin(null);
    }

    if (firstFile) {
      const cached = getCached(firstFile);
      setFirstLatest(getLatest(cached));
      setFirstMin(parseMinutes(firstFile));
      unsubFirst = subscribe(firstFile, (d) => setFirstLatest(getLatest(d)));
    } else {
      setFirstLatest(null);
      setFirstMin(null);
    }

    if (thirdFile) {
      const cached = getCached(thirdFile);
      setThirdLatest(getLatest(cached));
      setThirdMin(parseMinutes(thirdFile));
      unsubThird = subscribe(thirdFile, (d) => setThirdLatest(getLatest(d)));
    } else {
      setThirdLatest(null);
      setThirdMin(null);
    }

    return () => {
      if (unsubBase) unsubBase();
      if (unsubFirst) unsubFirst();
      if (unsubThird) unsubThird();
    };
  }, [baseFile, firstFile, thirdFile]);

  const computeSignal = () => {
    // Dynamic weights based on parsed minutes from filenames.
    // If 'first' (the primary 15-min view) is missing we consider it INCOMPLETE.
    if (firstLatest === null) return { text: 'INCOMPLETE', score: null };

    const mBase = baseMin || 1;
    const mFirst = firstMin || 1;
    const mThird = thirdMin || 1;

    // normalize weights so they sum to 1
    const totalM = mBase + mFirst + mThird;
    const wBase = mBase / totalM;
    const wFirst = mFirst / totalM;
    const wThird = mThird / totalM;

    const safe = (obj) => ({ ce: obj && typeof obj.CE === 'number' ? obj.CE : 0, pe: obj && typeof obj.PE === 'number' ? obj.PE : 0 });

    const b = safe(baseLatest);
    const f = safe(firstLatest);
    const t = safe(thirdLatest);

    const ceSum = (b.ce * wBase) + (f.ce * wFirst) + (t.ce * wThird);
    const peSum = (b.pe * wBase) + (f.pe * wFirst) + (t.pe * wThird);

    const score = ceSum - peSum;

    // choose instrument by absolute dominance
    const instrument = Math.abs(ceSum) >= Math.abs(peSum) ? 'call' : 'put';

    if (score > 0) return { text: `${instrument} buy`, score, ceSum, peSum, instrument };
    if (score < 0) return { text: `${instrument} sell`, score, ceSum, peSum, instrument };
    return { text: 'NEUTRAL', score, ceSum, peSum, instrument };
  };

  const sig = computeSignal();

  const formatVal = (obj) => (obj ? (typeof obj.CE === 'number' && typeof obj.PE === 'number' ? (obj.CE - obj.PE) : '–') : '–');

  return (
    <div className="buysignal">
      <div className="section">
        <div className="label">Buy Signal (next 15 min)</div>
        <div style={{ marginBottom: 10 }}>
          <strong>Decision:</strong>&nbsp;
          <span style={{ color: sig.text.includes('buy') ? '#2ecc40' : sig.text.includes('sell') ? '#ff4136' : '#999' }}>{sig.text.toUpperCase()}</span>
        </div>
        <div style={{ fontSize: 12, color: '#666' }}>
          <div>Base ({baseMin ? baseMin + 'm' : '–'}): {baseFile || '–'} → {formatVal(baseLatest)}</div>
          <div>First ({firstMin ? firstMin + 'm' : '–'}): {firstFile || '–'} → {formatVal(firstLatest)}</div>
          <div>Third ({thirdMin ? thirdMin + 'm' : '–'}): {thirdFile || '–'} → {formatVal(thirdLatest)}</div>
          <div>Score: {sig.score !== null ? sig.score.toFixed(2) : '–'}</div>
          <div>CE sum: {sig.ceSum !== undefined ? sig.ceSum.toFixed(2) : '–'} &nbsp; PE sum: {sig.peSum !== undefined ? sig.peSum.toFixed(2) : '–'}</div>
          <div>Instrument: {sig.instrument ? sig.instrument.toUpperCase() : '–'}</div>
        </div>

      </div>

      <FileSelect caption="Base" storageKey="buysignal.base" compact={true} showResult={false} sumsOnly={true} preserveBlank={true} baseData={getCached(baseFile) || []} />
      <FileSelect caption="First timeline" storageKey="buysignal.first" compact={true} showResult={true} sumsOnly={true} preserveBlank={true} baseData={getCached(baseFile) || []} />
      <FileSelect caption="Third timeline" storageKey="buysignal.third" compact={true} showResult={true} sumsOnly={true} preserveBlank={true} baseData={getCached(baseFile) || []} />
    </div>
  );
};

export default BuysignalScreen;